// Geometry vocabulary: vertical = x-stripe, horizontal = shelf tiers. SpineLevel.orient is never consulted.
import type { FlagSeverity, StudioDraft } from "./studioTypes";

export interface AestheticsWeights {
  blocking: number;
  cdt: number;
  fullness: number;
  ergonomics: number;
}

export interface TierLevel {
  label: string;
  values: readonly string[];
}

export interface AestheticsRules {
  brandAttr: string;
  tierAttr: string;
  sizeAttr: string;
  flavourAttr: string;
  verticalAttrs: readonly string[];
  /** Top shelf first; a tier is a level. Absent values are ungraded. */
  tierOrder: readonly TierLevel[];
  lastInBlock: readonly string[];
  bayOwners: readonly string[];
  bulkPounds: number;
  lowerShelfShare: number;
  jitterPacks: number;
  raggedZeroPacks: number;
  flagsPerHundredZero: number;
  severityPenalty: Readonly<Record<FlagSeverity, number>>;
  weights: AestheticsWeights;
  blockingSplit: { vertical: number; horizontal: number };
  cdtSplit: { detector: number; ordering: number };
}

export const DEFAULT_WEIGHTS: AestheticsWeights = {
  blocking: 40,
  cdt: 30,
  fullness: 20,
  ergonomics: 10,
};

export const DEFAULT_SEVERITY_PENALTY: Record<FlagSeverity, number> = {
  blocking: 1,
  toCheck: 0.4,
  toReview: 0.15,
};

const BASE_RULES: Omit<
  AestheticsRules,
  "brandAttr" | "tierAttr" | "sizeAttr" | "flavourAttr" | "verticalAttrs"
> = {
  tierOrder: [],
  lastInBlock: [],
  bayOwners: [],
  /** 0: ergonomics does not grade bulk. */
  bulkPounds: 0,
  lowerShelfShare: 0.5,
  jitterPacks: 1,
  raggedZeroPacks: 4,
  flagsPerHundredZero: 20,
  severityPenalty: DEFAULT_SEVERITY_PENALTY,
  weights: DEFAULT_WEIGHTS,
  blockingSplit: { vertical: 0.6, horizontal: 0.4 },
  cdtSplit: { detector: 0.6, ordering: 0.4 },
};

const matches = (value: string, pattern: RegExp) => pattern.test(value);

/** Roles fill positionally around the size level so they match detectExceptions; overrides win. */
export const resolveAestheticsRules = (
  draft: Pick<StudioDraft, "spine">,
  overrides: Partial<AestheticsRules> = {},
): AestheticsRules => {
  const attrs = draft.spine.map((level) => level.attr);
  const labels = draft.spine.map((level) => level.label ?? level.attr);
  const sizeIndex = (() => {
    const byAttr = attrs.findIndex((attr) => matches(attr, /size/i));
    if (byAttr >= 0) return byAttr;
    const byLabel = labels.findIndex((label) => matches(label, /size/i));
    return byLabel >= 0 ? byLabel : Math.max(0, attrs.length - 3);
  })();
  const sizeAttr = attrs[sizeIndex] ?? "size";
  // Empty when size is the top level so tier measures stand down instead of guessing.
  const tierAttr = sizeIndex > 0 ? attrs[sizeIndex - 1] : "";
  const brandAttr = attrs.length > 1 ? attrs[1] : (attrs[0] ?? "");
  const flavourAttr = attrs.length ? attrs[attrs.length - 1] : "";
  const verticalAttrs = attrs.slice(0, sizeIndex);

  return {
    ...BASE_RULES,
    brandAttr,
    tierAttr,
    sizeAttr,
    flavourAttr,
    verticalAttrs: verticalAttrs.length ? verticalAttrs : attrs.slice(0, 1),
    ...overrides,
    weights: { ...BASE_RULES.weights, ...overrides.weights },
    severityPenalty: { ...BASE_RULES.severityPenalty, ...overrides.severityPenalty },
    blockingSplit: { ...BASE_RULES.blockingSplit, ...overrides.blockingSplit },
    cdtSplit: { ...BASE_RULES.cdtSplit, ...overrides.cdtSplit },
  };
};
